/**
 * Styleboard Remove Dialog Component
 *
 * Confirmation dialog for removing a product from styleboards.
 */

import { DialogComponent } from '@theme/dialog';
import { StyleboardSelectorDialog } from '@theme/styleboard-selector-modal';

const DEFAULT_API_BASE_URL = `https://${window.location.host}/apps/jyapi`;

/**
 * Gets the notification function
 * @returns {{ success: Function, error: Function, info: Function } | null}
 */
function getNotification() {
  return /** @type {any} */ (window).notification || null;
}

class StyleboardRemoveDialog extends DialogComponent {
  /** @type {Array<{ id: string, name: string, selected: boolean }>} */
  #matchingStyleboards = [];

  /** @type {{ style: string, color?: string | null, type?: string } | null} */
  #product = null;

  /** @type {string} */
  #removeBtnDefaultLabel = '';

  get apiBaseUrl() {
    return Theme.api?.baseUrl || DEFAULT_API_BASE_URL;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getStyleboardsListEl() {
    const el = this.querySelector('[ref="styleboardsList"]');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getRemoveBtn() {
    const el = this.querySelector('[ref="removeBtn"]');
    return el instanceof HTMLButtonElement ? el : null;
  }

  /**
   * @param {boolean} disabled
   */
  #setRemoveBtnDisabled(disabled) {
    const removeBtn = this.#getRemoveBtn();
    if (!removeBtn) return;

    if (!this.#removeBtnDefaultLabel) {
      this.#removeBtnDefaultLabel = (removeBtn.textContent || '').trim();
    }

    removeBtn.disabled = disabled;
    if (!disabled && this.#removeBtnDefaultLabel) {
      removeBtn.textContent = this.#removeBtnDefaultLabel;
    }
  }

  /**
   * Builds API URL with session token for guest users
   * @param {string} endpoint
   * @returns {string}
   */
  #buildApiUrl(endpoint) {
    const url = new URL(`${this.apiBaseUrl}${endpoint}`, /^https?:\/\//i.test(this.apiBaseUrl) ? undefined : window.location.origin);
    if (typeof StyleboardSelectorDialog?.applySessionTokensToUrl === 'function') {
      StyleboardSelectorDialog.applySessionTokensToUrl(url);
    } else if (typeof StyleboardSelectorDialog?.getSessionToken === 'function') {
      const sessionToken = StyleboardSelectorDialog.getSessionToken();
      if (sessionToken) {
        if (url.pathname.includes('/retail/styleboards-list')) {
          url.searchParams.append('ids[]', sessionToken);
        } else {
          url.searchParams.set('session_token', sessionToken);
        }
      }
    }
    return url.toString();
  }

  /**
   * Resolves details endpoint identifier.
   * Prefers guest session token when available.
   * @param {string} fallbackId
   * @returns {string}
   */
  #getDetailsIdentifier(fallbackId) {
    return fallbackId || StyleboardSelectorDialog.getSessionToken() || '';
  }

  /**
   * Shows the dialog and fetches styleboards, checking which ones contain the product.
   * @param {{ style: string, color?: string | null, type?: string }} product
   */
  async showWithProduct(product) {
    this.#product = product;
    this.#matchingStyleboards = [];

    this.showDialog();
    await this.#loadAndCheckStyleboards();
  }

  /**
   * Fetches all styleboards and checks which ones contain the product.
   */
  async #loadAndCheckStyleboards() {
    const listEl = this.#getStyleboardsListEl();
    if (!listEl) return;

    this.#setRemoveBtnDisabled(true);
    listEl.innerHTML = '<p class="text-sm opacity-60">Loading styleboards...</p>';

    try {
      const allStyleboards = await this.#fetchStyleboardsList();

      if (allStyleboards.length === 0) {
        listEl.innerHTML = '<p class="text-sm opacity-60 text-center py-4">No styleboards found.</p>';
        return;
      }

      this.#matchingStyleboards = await this.#findMatchingStyleboards(allStyleboards);

      if (this.#matchingStyleboards.length === 0) {
        listEl.innerHTML = '<p class="text-sm opacity-60 text-center py-4">This product is not in any styleboards.</p>';
        this.#setRemoveBtnDisabled(true);
        return;
      }

      this.#renderStyleboardsList();
      this.#setRemoveBtnDisabled(false);
    } catch (error) {
      console.error('Error loading styleboards:', error);
      listEl.innerHTML = '<p class="text-sm text-red-600 text-center py-4">Failed to load styleboards. Please try again.</p>';
      this.#setRemoveBtnDisabled(true);
    }
  }

  /**
   * @returns {Promise<any[]>}
   */
  async #fetchStyleboardsList() {
    const response = await fetch(this.#buildApiUrl('/retail/styleboards-list'), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch styleboards');
    }

    const data = await response.json().catch(() => ({}));
    const allStyleboards = data?.data?.styleboards || [];
    return Array.isArray(allStyleboards) ? allStyleboards : [];
  }

  /**
   * @param {any} styleboard
   * @returns {string | null}
   */
  #getStyleboardIdentifier(styleboard) {
    const id = styleboard?.id || null;
    return typeof id === 'string' && id.trim() ? id : null;
  }

  /**
   * @param {any[]} allStyleboards
   * @returns {Promise<Array<{ id: string, name: string, selected: boolean }>>}
   */
  async #findMatchingStyleboards(allStyleboards) {
    const checks = await Promise.allSettled(
      allStyleboards.map(async (styleboard) => {
        const id = this.#getStyleboardIdentifier(styleboard);
        if (!id) return null;

        const contains = await this.#checkStyleboardContainsProduct(id);
        if (!contains) return null;

        return {
          id,
          name: styleboard?.name || 'Unnamed Style Board',
          selected: true,
        };
      })
    );

    return checks
      .filter((r) => r.status === 'fulfilled')
      .map((r) => /** @type {any} */(r).value)
      .filter(Boolean);
  }

  /**
   * Checks if a styleboard contains the current product.
   * @param {string} styleboardId
   * @returns {Promise<boolean>}
   */
  async #checkStyleboardContainsProduct(styleboardId) {
    if (!this.#product) return false;

    try {
      const detailsIdentifier = this.#getDetailsIdentifier(styleboardId);
      const response = await fetch(
        this.#buildApiUrl(`/retail/styleboards/${detailsIdentifier}/details`),
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const items = data.data?.styleboard?.items || [];

      // Check if any item matches our product (by style and color)
      return items.some((/** @type {{ style: string, color?: string | null }} */ item) => {
        const styleMatches = item.style === this.#product?.style;
        const colorMatches = (!this.#product?.color && !item.color) ||
          (this.#product?.color === item.color);
        return styleMatches && colorMatches;
      });
    } catch (error) {
      console.error(`Error checking styleboard ${styleboardId}:`, error);
      return false;
    }
  }

  /**
   * Renders the styleboards list with checkboxes.
   */
  #renderStyleboardsList() {
    const listEl = this.querySelector('[ref="styleboardsList"]');
    if (!listEl) return;

    if (this.#matchingStyleboards.length === 0) {
      listEl.innerHTML = '<p class="text-sm opacity-60 text-center py-4">No styleboards found.</p>';
      return;
    }

    // Render checkboxes similar to selector modal's radio buttons
    const itemsHtml = this.#matchingStyleboards
      .map((sb, index) => {
        const safeId = this.#escapeHtml(String(sb.id ?? ''));
        const safeName = this.#escapeHtml(sb.name || 'Unnamed Style Board');
        return `
          <label class="flex items-center gap-2 p-3 border border-foreground/10 rounded cursor-pointer transition-colors hover:bg-primary/4" for="remove-styleboard-${safeId}">
            <input
              type="checkbox"
              class="h-4.5 w-4.5 accent-white"
              name="remove-styleboard"
              value="${safeId}"
              id="remove-styleboard-${safeId}"
              ${sb.selected ? 'checked' : ''}
              onchange="this.closest('styleboard-remove-dialog').handleCheckboxChange(${index}, this.checked)"
            />
            <span class="text-primary text-sm">
              ${safeName}
            </span>
          </label>
        `;
      })
      .join('');

    listEl.innerHTML = itemsHtml;
  }

  /**
   * Handles checkbox change.
   * @param {number} index
   * @param {boolean} checked
   */
  handleCheckboxChange(index, checked) {
    if (this.#matchingStyleboards[index]) {
      this.#matchingStyleboards[index].selected = checked;
    }
  }

  /**
   * Handles the remove button click.
   */
  handleRemove = async () => {
    const selectedBoards = this.#matchingStyleboards.filter(sb => sb.selected);

    if (selectedBoards.length === 0) {
      const notify = getNotification();
      if (notify) {
        notify.error('Please select at least one styleboard');
      }
      return;
    }

    const removeBtn = this.#getRemoveBtn();
    if (removeBtn) {
      if (!this.#removeBtnDefaultLabel) {
        this.#removeBtnDefaultLabel = (removeBtn.textContent || '').trim();
      }
      removeBtn.disabled = true;
      removeBtn.textContent = 'Removing...';
    }

    try {
      // Remove from each selected styleboard
      const removePromises = selectedBoards.map(sb =>
        this.#removeProductFromStyleboard(sb.id)
      );

      const results = await Promise.allSettled(removePromises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;

      const notify = getNotification();
      if (successCount > 0) {
        if (notify) {
          notify.success(`Removed from ${successCount} styleboard${successCount > 1 ? 's' : ''}`);
        }

        // Dispatch event so button state can update
        document.dispatchEvent(new CustomEvent('product-removed-from-styleboard', {
          detail: { product: this.#product, removedFrom: selectedBoards.map(sb => sb.id) }
        }));
      }

      this.closeDialog();
    } catch (error) {
      console.error('Error removing from styleboards:', error);
      const notify = getNotification();
      if (notify) {
        notify.error('Failed to remove product', 'Please try again');
      }
    } finally {
      this.#setRemoveBtnDisabled(false);
    }
  };

  /**
   * Fetches CSRF token from server. Always fetches to ensure a valid token.
   * @returns {Promise<string | null>} The CSRF token or null if not available
   */
  async #getCsrfToken() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));
      const token = data?.token ?? data?.csrf_token ?? data?.csrfToken ?? data?._token ?? null;
      return token || null;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return null;
    }
  }

  /**
   * Removes a product from a styleboard.
   * @param {string} styleboardId
   * @param {boolean} [isRetry=false]
   * @returns {Promise<void>}
   */
  async #removeProductFromStyleboard(styleboardId, isRetry = false) {
    if (!this.#product) throw new Error('No product set');

    const csrfToken = await this.#getCsrfToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    const body = {
      style: this.#product.style,
      color: this.#product.color || '',
      type: this.#product.type || 'ecom',
      _token: csrfToken,
    };

    // Delete the item using POST to bypass Shopify Proxy DELETE restriction
    const response = await fetch(
      this.#buildApiUrl(`/retail/styleboards/${styleboardId}/items`),
      {
        method: 'DELETE',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      }
    );

    if (response.status === 419 && !isRetry) {
      const freshToken = await this.#getCsrfToken();
      if (freshToken) {
        return this.#removeProductFromStyleboard(styleboardId, true);
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to remove product');
    }
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('styleboard-remove-dialog')) {
  customElements.define('styleboard-remove-dialog', /** @type {typeof HTMLElement} */(/** @type {any} */ (StyleboardRemoveDialog)));
}

export { StyleboardRemoveDialog };
